/**
 * Shared Exercise Search Utility
 * ================================
 * Single source of truth for exercise name matching across the entire app.
 *
 * Features:
 *   - Bilingual: searches BOTH name AND name_es (matches in either)
 *   - Accent-normalized (ñ→n, á→a, etc.)
 *   - Tokenized AND matching (all words must appear, order-independent)
 *   - Optimized for ~1200 exercises with pre-built search index
 *
 * Usage:
 *   import { searchExercises, buildSearchIndex, searchWithIndex } from '../utils/exerciseSearch';
 *
 *   // Simple (no index):
 *   const results = searchExercises(query, exercises);
 *
 *   // Optimized (with index, for components that re-render often):
 *   const index = useMemo(() => buildSearchIndex(exercises), [exercises]);
 *   const results = searchWithIndex(query, index);
 */

/**
 * Remove diacritics/accents from a string.
 * "ñ"→"n", "á"→"a", "ü"→"u", etc.
 */
const normalize = (str) =>
  str
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Tokenize a query string into individual search words.
 * Splits on whitespace, filters empty tokens.
 */
const tokenize = (query) =>
  normalize(query)
    .split(/\s+/)
    .filter(Boolean);

/**
 * Build a combined searchable string from BOTH name fields.
 * Trainers use English terminology even in a Spanish-first app,
 * so we search both fields and match on either.
 *
 * The combined string is: "normalized_name | normalized_name_es"
 * Token matching against this combined string means a token can
 * appear in either the English or Spanish name.
 */
const getBilingualSearchText = (exercise) => {
  const name = exercise.name || exercise.exercise_name || '';
  const nameEs = exercise.name_es || exercise.exercise_name_es || '';
  return normalize(name) + ' | ' + normalize(nameEs);
};

/**
 * Check if ALL query tokens appear in the normalized text.
 * Order-independent AND matching.
 */
const matchesAllTokens = (tokens, normalizedText) => {
  for (let i = 0; i < tokens.length; i++) {
    if (!normalizedText.includes(tokens[i])) return false;
  }
  return true;
};

/**
 * searchExercises — Search exercises by name (bilingual).
 *
 * Searches BOTH name (English) and name_es (Spanish) simultaneously.
 * A match in either field counts. All query tokens must match (AND logic).
 *
 * @param {string} query - User input (can be empty)
 * @param {Array} exercises - Array of exercise objects
 * @param {string} _language - Ignored (kept for backward compatibility)
 * @returns {Array} Filtered exercises matching ALL query tokens
 */
export const searchExercises = (query, exercises, _language) => {
  if (!query || !query.trim()) return exercises;
  if (!exercises || exercises.length === 0) return [];

  const tokens = tokenize(query);
  if (tokens.length === 0) return exercises;

  return exercises.filter((exercise) => {
    const searchText = getBilingualSearchText(exercise);
    return matchesAllTokens(tokens, searchText);
  });
};

/**
 * buildSearchIndex — Pre-compute normalized names for faster repeated searches.
 * Use this with useMemo when the exercise list changes infrequently
 * but the search query changes on every keystroke.
 *
 * @param {Array} exercises - Array of exercise objects
 * @param {string} _language - Ignored (kept for backward compatibility)
 * @returns {Array} Array of { exercise, normalizedName }
 */
export const buildSearchIndex = (exercises, _language) => {
  if (!exercises) return [];
  return exercises.map((exercise) => ({
    exercise,
    normalizedName: getBilingualSearchText(exercise),
  }));
};

/**
 * searchWithIndex — Fast search using a pre-built index.
 *
 * @param {string} query - User input
 * @param {Array} index - Output of buildSearchIndex()
 * @returns {Array} Filtered exercise objects
 */
export const searchWithIndex = (query, index) => {
  if (!query || !query.trim()) return index.map((entry) => entry.exercise);
  if (!index || index.length === 0) return [];

  const tokens = tokenize(query);
  if (tokens.length === 0) return index.map((entry) => entry.exercise);

  const results = [];
  for (let i = 0; i < index.length; i++) {
    if (matchesAllTokens(tokens, index[i].normalizedName)) {
      results.push(index[i].exercise);
    }
  }
  return results;
};

export default searchExercises;
